import { ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Check, Search, UserPlus, X } from 'lucide-angular';
import { Colleague } from '../../../core/models/chat.model';
import { TranslationService } from '../../../core/services/translation.service';
import { degradeAvatar, initiale, urlPhoto } from './avatars-appel';
import { PhotoAppelComponent } from './photo-appel.component';

/**
 * Feuille « Ajouter a l'appel » — maquette « Appels — palette claire », ecran 8 (photo ou initiale, clair par defaut, sombre sous `html.dark`).
 *
 * On CHOISIT d'abord (rond a droite de chaque ligne), puis on invite d'un seul geste par
 * le bouton du bas, dont le libelle dit combien et qui. Le choix est local a la feuille :
 * rien n'est envoye avant l'appui sur ce bouton.
 *
 * ABSENT DE LA MAQUETTE, VOLONTAIREMENT : le point vert « en ligne » et le prefixe « En
 * ligne · » / « Hors ligne · ». `Colleague.status` vaut 'offline' en dur : aucune presence
 * reelle n'existe dans le produit, afficher un etat serait mentir a chaque ligne.
 */
@Component({
    selector: 'app-ajouter-participant',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, NgTemplateOutlet, LucideAngularModule, PhotoAppelComponent],
    template: `
    <div class="fixed inset-0 z-[60]" role="dialog" aria-modal="true" [attr.aria-label]="t().calls.add_to_call">
      <div class="absolute inset-0 bg-surface-900/35 dark:bg-[rgba(2,1,3,0.45)]" (click)="fermer.emit()" aria-hidden="true"></div>

      <div class="absolute bottom-0 inset-x-0 flex flex-col max-h-[620px] rounded-t-[28px] bg-white text-surface-900 shadow-[0_-20px_60px_-10px_rgba(15,23,42,0.3)]
                  dark:text-[#f4f4fe] dark:bg-[linear-gradient(180deg,#1c1550_0%,#100c33_100%)] dark:shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.6)] dark:border-t dark:border-white/[0.08]">
        <div class="flex-none flex justify-center pt-2.5" aria-hidden="true">
          <div class="w-9 h-1 rounded-full bg-surface-300 dark:bg-white/[0.18]"></div>
        </div>

        <div class="flex-none px-5 pt-4 pb-1.5 flex items-center justify-between gap-3">
          <div class="min-w-0">
            <h2 class="text-[17px] leading-[normal] font-extrabold">{{ t().calls.add_to_call }}</h2>
            <div class="text-xs leading-[normal] mt-0.5 truncate text-surface-500 dark:text-[#a5a1d6]">{{ sousTitre() }}</div>
          </div>
          <button type="button" (click)="fermer.emit()" class="bouton w-11 h-11 rounded-full flex items-center justify-center flex-none border bg-white border-surface-200 text-surface-700
                  dark:bg-white/[0.08] dark:border-transparent dark:text-[#f4f4fe]" [attr.aria-label]="t().calls.close">
            <lucide-icon [img]="X" [size]="16" aria-hidden="true"></lucide-icon>
          </button>
        </div>

        <div class="flex-none px-5 pt-2 pb-3.5">
          <label class="flex items-center gap-2 rounded-xl px-3.5 min-h-11 border bg-surface-100 border-surface-200 dark:bg-white/[0.07] dark:border-white/[0.09]">
            <lucide-icon [img]="Search" [size]="16" class="text-surface-500 dark:text-[#7d78b0]" aria-hidden="true"></lucide-icon>
            <input type="text" [ngModel]="recherche()" (ngModelChange)="recherche.set($event)"
                   [placeholder]="t().calls.search_colleague" [attr.aria-label]="t().calls.search_colleague"
                   class="flex-1 min-w-0 bg-transparent text-[13.5px] outline-none placeholder:text-surface-500 dark:placeholder:text-[#7d78b0]" />
          </label>
        </div>

        <div class="flex-1 overflow-y-auto pb-1.5">
          @if (dansConversation().length > 0) {
            <p class="px-5 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-surface-500 dark:text-[#7d78b0]">{{ t().calls.in_this_conversation }}</p>
            @for (c of dansConversation(); track c.id) {
              <ng-container *ngTemplateOutlet="ligne; context: { $implicit: c }" />
            }
          }
          @if (autres().length > 0) {
            <p class="px-5 pt-3.5 pb-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-surface-500 dark:text-[#7d78b0]">{{ t().calls.other_colleagues }}</p>
            @for (c of autres(); track c.id) {
              <ng-container *ngTemplateOutlet="ligne; context: { $implicit: c }" />
            }
          }
        </div>

        <div class="flex-none px-5 pt-3" style="padding-bottom:calc(env(safe-area-inset-bottom,0px) + 22px)">
          <button type="button" (click)="envoyer()" [disabled]="choisis().length === 0"
                  class="bouton w-full min-h-12 rounded-full text-[14.5px] font-bold text-white disabled:opacity-45 bg-primary-600
                         dark:bg-[linear-gradient(160deg,#a5b4fc,#4f46e5)] dark:shadow-[0_12px_24px_-8px_rgba(79,70,229,0.55)]">
            {{ libelleAction() }}
          </button>
        </div>
      </div>
    </div>

    <ng-template #ligne let-c>
      <button type="button" (click)="basculer(c)" [attr.aria-pressed]="estChoisi(c)"
              class="bouton ligne w-full min-h-11 flex items-center gap-3 px-5 py-[11px] text-left" [class]="estChoisi(c) ? 'bg-surface-50 dark:bg-transparent' : ''">
        <span class="avatar-ligne w-10 h-10 rounded-full overflow-hidden flex items-center justify-center text-sm font-bold flex-none border bg-surface-100 border-surface-200 text-surface-700
                     dark:border-0 dark:text-white" [style.--degrade]="degrade(c)">
          <app-photo-appel [photo]="photo(c)" [nom]="c.name" [initiales]="initiale(c.name)" />
        </span>
        <span class="flex-1 min-w-0">
          <span class="block text-[14.5px] font-semibold truncate">{{ c.name }}</span>
          <span class="block text-xs truncate text-surface-500 dark:text-[#a5a1d6]">{{ c.role }}</span>
        </span>
        @if (estChoisi(c)) {
          <span class="w-[30px] h-[30px] rounded-full flex items-center justify-center flex-none text-white bg-primary-600 dark:bg-[linear-gradient(160deg,#a5b4fc,#4f46e5)]">
            <lucide-icon [img]="Check" [size]="15" aria-hidden="true"></lucide-icon>
          </span>
        } @else {
          <span class="w-[30px] h-[30px] rounded-full flex items-center justify-center flex-none border-[1.5px] border-surface-300 text-surface-600 dark:border-white/[0.18] dark:text-[#c7c3f5]">
            <lucide-icon [img]="UserPlus" [size]="14" aria-hidden="true"></lucide-icon>
          </span>
        }
      </button>
    </ng-template>
  `,
    styles: [
        `
      .bouton { transition:transform .15s ease, background .15s ease; }
      .bouton:active { transform:scale(0.97); }
      .ligne:active { background:rgba(15,23,42,0.05); }
      :host-context(.dark) .avatar-ligne { background:var(--degrade); }
    `,
    ],
})
export class AjouterParticipantComponent {
    /** Nom de l'appel (l'interlocuteur) et nombre de personnes deja presentes, pour le sous-titre. */
    readonly nomAppel = input.required<string>();
    readonly dejaPresents = input.required<number>();
    readonly dansConversation = input.required<Colleague[]>();
    readonly autres = input.required<Colleague[]>();
    readonly recherche = model('');

    readonly fermer = output<void>();
    readonly inviter = output<Colleague[]>();

    readonly t = inject(TranslationService).t;

    readonly Check = Check;
    readonly Search = Search;
    readonly UserPlus = UserPlus;
    readonly X = X;
    readonly initiale = initiale;

    private readonly selection = signal<ReadonlySet<string>>(new Set());

    /** Choisis, dans l'ordre d'affichage, et seulement parmi ceux encore listes (la recherche peut en masquer). */
    readonly choisis = computed(() => {
        const ids = this.selection();
        return [...this.dansConversation(), ...this.autres()].filter((c) => ids.has(String(c.id)));
    });

    readonly sousTitre = computed(() => {
        const n = this.dejaPresents();
        const presents = (n === 1 ? this.t().calls.already_present_one : this.t().calls.already_present_many).replace('{n}', String(n));
        return `${this.nomAppel()} · ${presents}`;
    });

    readonly libelleAction = computed(() => {
        const choisis = this.choisis();
        if (choisis.length === 0) return this.t().calls.invite;
        if (choisis.length === 1) return this.t().calls.invite_one.replace('{name}', choisis[0].name);
        return this.t().calls.invite_many.replace('{n}', String(choisis.length));
    });

    /** La photo de profil du collegue, quand il en a une. */
    photo(c: Colleague): string | undefined {
        return urlPhoto(c.avatarUrl);
    }

    degrade(c: Colleague): string {
        return degradeAvatar(c.name);
    }

    estChoisi(c: Colleague): boolean {
        return this.selection().has(String(c.id));
    }

    basculer(c: Colleague): void {
        const suivant = new Set(this.selection());
        const cle = String(c.id);
        if (suivant.has(cle)) suivant.delete(cle);
        else suivant.add(cle);
        this.selection.set(suivant);
    }

    envoyer(): void {
        const choisis = this.choisis();
        if (choisis.length > 0) this.inviter.emit(choisis);
    }
}
